// Relationship Blueprint — question catalogue.
// Each question has: id, category, text, options.
// Option shape for Q1-Q18: { text, score } — score is null for "Not applicable" (Q4 only).
// Q19: multi-select strings; Q20: single-select strings.

export const CATEGORIES = {
    disconnection: "Disconnection",
    distress: "Relationship Distress",
    stuckness: "Stuckness",
    readiness: "Readiness for Change",
};

export const SCORED = Array.from({ length: 18 }, (_, i) => i + 1);

export const QUESTIONS = [
    // CATEGORY 1 — DISCONNECTION
    {
        id: 1,
        category: "disconnection",
        text: "How emotionally connected do you currently feel with your partner?",
        options: [
            { text: "Very connected — I generally feel seen, understood and emotionally close.", score: 1 },
            { text: "Mostly connected, although we have moments of distance.", score: 2 },
            { text: "Sometimes connected and sometimes distant.", score: 3 },
            { text: "Mostly disconnected — emotional closeness feels limited.", score: 4 },
            { text: "Very disconnected — I often feel lonely even within the relationship.", score: 5 },
        ],
    },
    {
        id: 2,
        category: "disconnection",
        text: "How comfortable are you sharing your true feelings and needs with your partner?",
        options: [
            { text: "Very comfortable — I generally feel able to be myself.", score: 1 },
            { text: "Mostly comfortable, although some conversations are difficult.", score: 2 },
            { text: "It depends on the situation.", score: 3 },
            { text: "I frequently hold things back to avoid conflict, rejection or misunderstanding.", score: 4 },
            { text: "I rarely feel able to express what I genuinely feel or need.", score: 5 },
        ],
    },
    {
        id: 3,
        category: "disconnection",
        text: "When you need emotional support, how supported do you generally feel by your partner?",
        options: [
            { text: "Very supported — I generally feel heard and cared for.", score: 1 },
            { text: "Mostly supported.", score: 2 },
            { text: "Sometimes supported and sometimes alone.", score: 3 },
            { text: "Rarely supported — I often feel misunderstood or emotionally unsupported.", score: 4 },
            { text: "I have largely stopped expecting emotional support from my partner.", score: 5 },
        ],
    },
    {
        id: 4,
        category: "disconnection",
        text: "How satisfied are you with the physical and intimate connection in your relationship?",
        options: [
            { text: "Very satisfied — it feels connected and mutually fulfilling.", score: 1 },
            { text: "Mostly satisfied.", score: 2 },
            { text: "Something feels missing, but it is not a major concern.", score: 3 },
            { text: "We have become significantly physically or intimately disconnected.", score: 4 },
            { text: "Our intimate connection is a major source of dissatisfaction or distress.", score: 5 },
            { text: "Not applicable.", score: null },
        ],
    },
    {
        id: 5,
        category: "disconnection",
        text: "Overall, how satisfied do you currently feel in this relationship?",
        options: [
            { text: "Very satisfied.", score: 1 },
            { text: "Mostly satisfied.", score: 2 },
            { text: "Neither satisfied nor dissatisfied.", score: 3 },
            { text: "Mostly dissatisfied.", score: 4 },
            { text: "Deeply dissatisfied.", score: 5 },
        ],
    },
    // CATEGORY 2 — RELATIONSHIP DISTRESS
    {
        id: 6,
        category: "distress",
        text: "How much emotional distress does your relationship situation currently cause you?",
        options: [
            { text: "Almost none.", score: 1 },
            { text: "Occasionally.", score: 2 },
            { text: "Regularly.", score: 3 },
            { text: "A lot.", score: 4 },
            { text: "It frequently feels overwhelming.", score: 5 },
        ],
    },
    {
        id: 7,
        category: "distress",
        text: "How much time do you spend thinking, worrying or analysing your relationship?",
        options: [
            { text: "Very little — it occupies a healthy amount of mental space.", score: 1 },
            { text: "Occasionally more than I would like.", score: 2 },
            { text: "Frequently.", score: 3 },
            { text: "Most days.", score: 4 },
            { text: "It occupies a very large amount of my mental and emotional space.", score: 5 },
        ],
    },
    {
        id: 8,
        category: "distress",
        text: "How much is your relationship situation affecting other areas of your life, such as work, sleep, confidence, friendships, interests or daily functioning?",
        options: [
            { text: "Hardly at all.", score: 1 },
            { text: "Slightly.", score: 2 },
            { text: "Moderately.", score: 3 },
            { text: "Significantly.", score: 4 },
            { text: "Multiple areas of my life are being affected.", score: 5 },
        ],
    },
    {
        id: 9,
        category: "distress",
        text: "How emotionally drained do you currently feel because of this relationship?",
        options: [
            { text: "Not drained.", score: 1 },
            { text: "Occasionally drained.", score: 2 },
            { text: "Moderately drained.", score: 3 },
            { text: "Frequently drained.", score: 4 },
            { text: "Emotionally exhausted.", score: 5 },
        ],
    },
    {
        id: 10,
        category: "distress",
        text: "If your relationship remained exactly as it is today for another two years, how would you feel?",
        options: [
            { text: "Happy or content.", score: 1 },
            { text: "Mostly okay, although I would like some improvement.", score: 2 },
            { text: "Disappointed.", score: 3 },
            { text: "Very unhappy.", score: 4 },
            { text: "I strongly do not want to continue experiencing the relationship this way.", score: 5 },
        ],
    },
    // CATEGORY 3 — STUCKNESS
    {
        id: 11,
        category: "stuckness",
        text: "How often do the same relationship problems or patterns keep repeating despite attempts to resolve them?",
        options: [
            { text: "Rarely.", score: 1 },
            { text: "Occasionally.", score: 2 },
            { text: "Sometimes.", score: 3 },
            { text: "Frequently.", score: 4 },
            { text: "Almost constantly — the same cycle keeps returning.", score: 5 },
        ],
    },
    {
        id: 12,
        category: "stuckness",
        text: "How clearly do you understand what is happening in your relationship?",
        options: [
            { text: "I don't currently see a significant problem or repeating pattern.", score: 1 },
            { text: "Something feels wrong, but I don't really understand what is happening.", score: 2 },
            { text: "I am beginning to understand the pattern.", score: 3 },
            { text: "I understand what is happening quite clearly.", score: 4 },
            { text: "I understand what is happening, but I still struggle to change my response.", score: 5 },
        ],
    },
    {
        id: 13,
        category: "stuckness",
        text: "When you know something isn't working for you, how easy is it to act on what you know?",
        options: [
            { text: "I can generally take appropriate action.", score: 1 },
            { text: "Mostly manageable, although it can sometimes be uncomfortable.", score: 2 },
            { text: "Sometimes difficult.", score: 3 },
            { text: "Very difficult.", score: 4 },
            { text: "I often know what I need to do but feel unable to do it.", score: 5 },
        ],
    },
    {
        id: 14,
        category: "stuckness",
        text: "How difficult is it for you to maintain boundaries in this relationship?",
        options: [
            { text: "Not difficult.", score: 1 },
            { text: "Slightly difficult.", score: 2 },
            { text: "Sometimes difficult.", score: 3 },
            { text: "Very difficult.", score: 4 },
            { text: "I frequently reverse or abandon my boundaries.", score: 5 },
        ],
    },
    {
        id: 15,
        category: "stuckness",
        text: "How often do fear, guilt, attachment, hope or uncertainty stop you from doing what you believe is right for you?",
        options: [
            { text: "Rarely.", score: 1 },
            { text: "Occasionally.", score: 2 },
            { text: "Sometimes.", score: 3 },
            { text: "Frequently.", score: 4 },
            { text: "Almost always.", score: 5 },
        ],
    },
    // CATEGORY 4 — READINESS FOR CHANGE
    {
        id: 16,
        category: "readiness",
        text: "How strongly do you want something about your current relationship situation to change?",
        options: [
            { text: "I don't feel much needs to change.", score: 1 },
            { text: "A few things could improve.", score: 2 },
            { text: "I definitely want some things to change.", score: 3 },
            { text: "I strongly want change.", score: 4 },
            { text: "I do not want to continue experiencing things as they currently are.", score: 5 },
        ],
    },
    {
        id: 17,
        category: "readiness",
        text: "How ready are you to work on your own patterns, responses, communication or boundaries?",
        options: [
            { text: "I am not currently interested in working on this.", score: 1 },
            { text: "I am curious, but not ready to actively work on it.", score: 2 },
            { text: "I am somewhat ready.", score: 3 },
            { text: "I am ready.", score: 4 },
            { text: "I am very ready to actively work on change.", score: 5 },
        ],
    },
    {
        id: 18,
        category: "readiness",
        text: "Which statement best describes where you are right now?",
        options: [
            { text: "I am generally satisfied and mainly interested in understanding my relationship better.", score: 1 },
            { text: "Something feels off, but I am still trying to understand what.", score: 2 },
            { text: "I understand some of the problems and want improvement.", score: 3 },
            { text: "I know something needs to change, but I am struggling to make it happen.", score: 4 },
            { text: "I am ready to actively work toward changing my own patterns, responses, boundaries or decisions.", score: 5 },
        ],
    },
    // PERSONALIZATION — UNSCORED
    {
        id: 19,
        category: "personal",
        multi: true,
        text: "What would you most like to change or understand right now?",
        subtext: "Select all that apply.",
        options: [
            "Emotional connection with my partner",
            "Physical or intimate connection",
            "Communication",
            "Setting and maintaining boundaries",
            "Repetitive relationship patterns",
            "Emotional dependency or attachment",
            "Clarity about whether I want to stay or leave",
            "Detaching from or moving forward from someone",
            "Understanding what I actually want from relationships",
            "Something else",
        ],
    },
    {
        id: 20,
        category: "personal",
        multi: false,
        text: "What kind of support are you looking for right now?",
        options: [
            "I am mainly exploring.",
            "I want to understand myself better.",
            "I want tools I can use myself.",
            "I am considering professional support.",
            "I am actively looking for professional support.",
        ],
    },
];

export const COUNTRY_CODES = [
    { code: "+91", name: "India" },
    { code: "+1", name: "USA / Canada" },
    { code: "+44", name: "United Kingdom" },
    { code: "+61", name: "Australia" },
    { code: "+971", name: "UAE" },
    { code: "+65", name: "Singapore" },
    { code: "+974", name: "Qatar" },
    { code: "+973", name: "Bahrain" },
    { code: "+966", name: "Saudi Arabia" },
    { code: "+64", name: "New Zealand" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
    { code: "+81", name: "Japan" },
    { code: "+82", name: "South Korea" },
    { code: "+27", name: "South Africa" },
    { code: "+234", name: "Nigeria" },
    { code: "+92", name: "Pakistan" },
    { code: "+880", name: "Bangladesh" },
    { code: "+94", name: "Sri Lanka" },
];
